<template>
	<v-card>
		<v-card-title
			class="d-flex align-center"
		>
			<v-icon
				icon="$cashMultiple"
				start
			/>
			<translate>Enter Damage Invoice</translate>
		</v-card-title>
		<v-card-text>
			<v-form ref="general">
				<v-row>
					<v-col>
						<v-select
							v-model="supplier"
							variant="outlined"
							return-object
							:label="$gettext('Recovery or Replacement Company')"
							:items="suppliers"
							item-title="serviceCenter"
							:rules="[v => (!!v && v.taxtype.name !== 'notSelected') ||'Please choose a Company']"
						/>
					</v-col>
					<v-col>
						<v-select
							v-model="invoiceType"
							variant="outlined"
							return-object
							required
							:label="$gettext('Document Type')"
							:items="invoiceTypes"
							item-title="type"
							:rules="[v => !!v ||'Please choose a Document Type']"
						/>
					</v-col>
					<v-col>
						<v-select
							v-model="damageType"
							variant="outlined"
							:label="$gettext('Damage Type')"
							:items="damageTypes"
							item-title="type"
							item-value="type"
							:rules="[v => !!v ||'Please choose a Damage Type']"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col cols="6">
						<date-picker
							v-model="invoiceDate"
							:label="$gettext('Document Date')"
							:max="maxDate"
							v-bind="$attrs"
							:rules="[v => !!v ||'Please choose a Document Date']"
							name="document_date"
						/>
					</v-col>
					<v-col
						v-if="invoiceType.type === 'Invoice'"
						cols="6"
					>
						<date-picker
							v-model="bookDate"
							:default-date="invoiceDate"
							:label="$gettext('Invoice Book Date')"
							:max="maxDate"
							v-bind="$attrs"
							:rules="[v => !!v ||'Please choose a Invoice Book Date']"
							name="invoice_date"
						/>
					</v-col>
				</v-row>
				<v-row>
					<v-col>
						<v-text-field
							v-model="referenceNumber"
							variant="outlined"
							:label="$gettext('Reference Number')"
							:rules="[v => !!v ||'Please add a Reference Number']"
						/>
					</v-col>
					<v-col>
						<v-text-field
							v-model="comment"
							variant="outlined"
							:label="$gettext('Comments')"
						/>
					</v-col>
					<v-divider />
				</v-row>
				<v-row>
					<v-col>
						<v-radio-group v-model="includeVAT">
							<template #label>
								<div><translate>Amount Includes VAT?</translate></div>
							</template>
							<v-radio
								:value="true"
								color="primary"
								:label="$gettext('Includes VAT')"
							/>
							<v-radio
								:value="false"
								color="primary"
								:label="$gettext('Excludes VAT')"
							/>
						</v-radio-group>
					</v-col>
					<v-col>
						<price-field
							v-model="price"
							:label="$gettext('Amount')"
							allow-negative
						/>
						<v-text-field
							variant="outlined"
							:label="$gettext('VAT Type')"
							:model-value="VatType"
							disabled
						/>
					</v-col>
					<v-col>
						<price-field
							:model-value="priceExcludeVat.toFixed(2)"
							:label="$gettext('Amount Excluding VAT')"
							disabled
						/>
						<price-field
							:model-value="vat.toFixed(2)"
							:label="$gettext('VAT')"
							disabled
						/>
						<price-field
							:model-value="totalPrice.toFixed(2)"
							:label="$gettext('Total Amount Including VAT')"
							disabled
						/>
					</v-col>
					<v-divider />
				</v-row>
				<v-row>
					<v-col cols="2">
						<price-field
							:model-value="Number(reserve).toFixed(2)"
							:label="$gettext('Old Reserve')"
							prefix="€"
							disabled
						/>
					</v-col>
					<v-col cols="2">
						<price-field
							:model-value="newReserve.toFixed(2)"
							:label="$gettext('Reserve after Import')"
							prefix="€"
							disabled
						/>
					</v-col>
					<v-col>
						<translate
							v-if="(reserveIsNegative || reserveIsPositive) && price !== '' && invoiceType.type === 'Invoice'"
							class="text-red"
						>
							The reserve is not 0 after entering this invoice. Do we expect more costs for this claim?
						</translate>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-card-actions>
			<v-row>
				<v-col v-if="(reserveIsNegative || reserveIsPositive) && price !== '' && invoiceType.type === 'Invoice'">
					<v-row>
						<v-col class="py-0">
							<translate>We don't expect any more costs on this claim</translate>
						</v-col>
					</v-row>
					<v-row>
						<v-col class="pt-0">
							<v-btn
								color="primary"
								:disabled="loading"
								@click="reserveReset = true; submit();"
							>
								<translate>
									Submit and set reserve to 0
								</translate>
							</v-btn>
						</v-col>
					</v-row>
				</v-col>

				<v-col>
					<v-row>
						<v-col class="py-0">
							<translate
								v-if="newReserve.toFixed(2) > 0 && price !== '' && invoiceType.type === 'Invoice'"
								:translate-params="{amount: newReserve.toFixed(2)}"
							>
								We expect another invoice or the remaining reserve of €%{amount}
							</translate>
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex justify-end pt-0">
							<v-btn
								v-if="newReserve.toFixed(2) >= 0"
								color="primary"
								:disabled="loading"
								@click="reserveReset = false; submit();"
							>
								<translate>
									Save
								</translate>
							</v-btn>
						</v-col>
					</v-row>
				</v-col>
			</v-row>
		</v-card-actions>
	</v-card>
</template>

<script>
import {
	axios,
} from '../../services/axios-service';
import displayError from 'laravel-front-library/src/utils/displayError/displayError';
import PriceField from '../atoms/PriceField.vue';
import DatePicker from '../atoms/DatePicker.vue';
import {
	inform,
} from 'laravel-front-library';

export default {
	name: 'DamageInvoiceCard',
	components: {
		DatePicker,
		PriceField
	},
	props: {
		reserve: {
			type: String,
			required: true
		},
		claimId: {
			type: Number,
			required: true
		}
	},
	data () {
		return {
			stepper: 1,
			supplier: {
				serviceCenter: 'Not selected',
				taxtype: {
					name: 'notSelected'
				}, taxrate: {
					value: 0.21
				}
			},
			suppliers: [],
			invoiceType: null,
			invoiceTypes: [],
			damageType: null,
			damageTypes: [],
			referenceNumber: null,
			comment: null,
			includeVAT: true,
			price: '',
			invoiceDate: null,
			bookDate: null,
			minDate: this.getMinDate(),
			maxDate: new Date().toISOString().split('T')[0],
			reserveReset: false,
			loading: false,
		};
	},

	computed: {
		VatType: function () {
			if (this.supplier) {
				return this.$gettext(this.supplier.taxtype.name) + ' (' + this.supplier.taxrate.value * 100 + ')%';
			}
			return '';
		},
		priceExcludeVat: function () {
			if (this.price) {
				if (this.includeVAT) {
					return this.price / (1 + this.supplier.taxrate.value);
				}
				return Number(this.price);
			}
			return 0.00;
		},
		vat: function () {
			if (this.price) {
				if (this.includeVAT) {
					return this.price - this.price / (1 + this.supplier.taxrate.value);
				}
				return this.price * this.supplier.taxrate.value;
			}
			return 0.00;
		},
		totalPrice: function () {
			if (this.price) {
				if (this.includeVAT) {
					return Number(this.price);
				}
				return Number(this.price) + this.vat;
			}
			return 0.00;
		},
		newReserve: function () {
			if (this.invoiceType.type === 'Invoice') {
				return Number(this.reserve) - this.totalPrice;
			}
			return this.totalPrice;
		},
		reserveIsNegative: function () {
			return this.newReserve.toFixed(2) < 0;
		},
		reserveIsPositive: function () {
			return this.newReserve.toFixed(2) > 0;
		}
	},

	created () {
		this.getSuppliers();
		this.getDamagecategories();
		const permission = localStorage.getItem('permissions').includes('claimdamages.create');

		if (permission) {
			this.invoiceTypes.push({
				id: 0, type: 'Invoice'
			});
		}
		this.invoiceTypes.push({
			id: 1, type: 'Quote'
		});
		this.invoiceType = this.invoiceTypes[0];
	},

	methods: {
		/**
		 * Get the minimum date. Includes last month if it has not been a week since start of month
		 *
		 * @returns {string} Date in YYYY-MM-DD format
		 */
		getMinDate () {
			const date = new Date();

			if(date.getDate() <= 7) {
				date.setMonth(date.getMonth() - 1);
			}
			date.setDate(1);
			return date.toISOString().split('T')[0];
		},
		/**
		 * Submit axios post.
		 */
		async submit () {
			const {
				valid
			} = await this.$refs.general.validate();

			if (valid) {
				this.loading = true;
				axios.post('/bo/claims/damageinsert', {
					id: this.claimId,
					invoiceType: this.invoiceType.id,
					damageType: this.damageType,
					amountDamage: this.totalPrice.toFixed(2),
					invoiceDate: this.invoiceDate,
					bookDate: this.bookDate ? this.bookDate : this.invoiceDate,
					invoiceReference: this.referenceNumber,
					messageDamage: this.comment,
					supplierId: this.supplier.id,
					reserveReset: this.reserveReset
				}).then(({
					data: data
				}) => {
					if (data) {
						inform(data);
					}
					setTimeout(() => {
						window.location.reload();
					}, 3000);
				}).catch(({
					response
				}) => {
					let message;

					if (response.data && response.data.message) {
						message = response.data.message;
					} else {
						message = this.$gettext('Something went wrong while marking the file as processed');
					}
					displayError(message);
					this.loading = false;
				});
			}
		},
		/**
		 * Get suppliers
		 */
		getSuppliers () {
			axios.get('/api/v0/suppliers').then(({
				data
			}) => {
				this.suppliers = data.sort((p1, p2) => (p1.serviceCenter > p2.serviceCenter));
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.captureException(error);
			});
		},

		/**
		 * get damage categories
		 */
		getDamagecategories () {
			axios.get('/api/v0/damagecategories').then(({
				data
			}) => {
				this.damageTypes = data.sort((p1, p2) => (p1.type > p2.type));
			}).catch((error) => {
				displayError(this.$gettext('Something has gone wrong during data retrieval'));
				window.captureException(error);
			});
		},
	}
};
</script>
